import Link from 'next/link';
import { ReactNode } from 'react';

type Action = {
  href: string;
  label: string;
  variant?: 'primary' | 'secondary';
};

type PageHeroProps = {
  eyebrow: string;
  title: string;
  description: string;
  actions?: Action[];
  aside?: ReactNode;
};

const actionClass = (variant: 'primary' | 'secondary' = 'primary') =>
  `inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-center text-sm font-semibold transition sm:w-auto ${
    variant === 'primary'
      ? 'bg-[var(--theme-dark)] text-[var(--theme-accent-soft)] hover:bg-[var(--theme-dark-soft)]'
      : 'border border-[rgba(51,53,51,0.2)] bg-[var(--theme-paper-warm)] text-[var(--theme-dark)] hover:border-[rgba(32,32,32,0.35)] hover:bg-[var(--theme-accent-soft)]'
  }`;

export const ActionLink = ({
  href,
  label,
  variant = 'primary',
}: Action) => {
  if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('http')) {
    return (
      <a href={href} className={actionClass(variant)}>
        {label}
      </a>
    );
  }

  return (
    <Link href={href} className={actionClass(variant)}>
      {label}
    </Link>
  );
};

export const PageHero = ({
  eyebrow,
  title,
  description,
  actions = [],
  aside,
}: PageHeroProps) => {
  return (
    <section className='px-4 pb-10 pt-10 sm:px-6 lg:px-8 lg:pt-14'>
      <div className='mx-auto grid max-w-7xl gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.8fr)] lg:items-center'>
        <div className='text-center lg:text-left'>
          <div className='mb-4 inline-flex rounded-full bg-[var(--theme-dark)] px-4 py-2 text-xs font-bold uppercase tracking-[0.24em] text-[var(--theme-accent-soft)]'>
            {eyebrow}
          </div>
          <h1 className='max-w-4xl text-[2rem] font-black leading-tight tracking-tight text-[var(--theme-dark)] md:text-5xl lg:text-6xl'>
            {title}
          </h1>
          <p className='mt-5 max-w-3xl text-lg leading-8 text-[var(--theme-dark-soft)] md:text-xl'>
            {description}
          </p>
          {actions.length > 0 && (
            <div className='mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center lg:justify-start'>
              {actions.map((action) => (
                <ActionLink key={`${action.href}-${action.label}`} {...action} />
              ))}
            </div>
          )}
        </div>
        <div className='rounded-[2rem] border border-[rgba(51,53,51,0.15)] bg-[linear-gradient(180deg,var(--theme-paper-warm)_0%,var(--theme-surface)_100%)] p-6 text-center shadow-[0_30px_80px_rgba(32,32,32,0.08)] lg:text-left'>
          {aside}
        </div>
      </div>
    </section>
  );
};

export const SectionBlock = ({
  title,
  children,
  id,
}: {
  title: string;
  children: ReactNode;
  id?: string;
}) => (
  <section id={id} className='px-4 py-8 sm:px-6 lg:px-8'>
    <div className='mx-auto max-w-7xl'>
      <h2 className='text-[1.75rem] font-black tracking-tight text-[var(--theme-dark)] md:text-4xl'>
        {title}
      </h2>
      <div className='mt-6 text-base leading-8 text-[var(--theme-dark-soft)] md:text-lg'>
        {children}
      </div>
    </div>
  </section>
);

export const CardGrid = ({
  items,
}: {
  items: Array<{ title: string; body: string }>;
}) => (
  <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
    {items.map((item) => (
      <article
        key={item.title}
        className='rounded-[1.75rem] border border-[rgba(51,53,51,0.15)] bg-[linear-gradient(180deg,var(--theme-paper-warm)_0%,var(--theme-surface)_100%)] p-6 shadow-[0_12px_32px_rgba(32,32,32,0.06)]'
      >
        <h3 className='text-xl font-black text-[var(--theme-dark)]'>{item.title}</h3>
        <p className='mt-3 text-base leading-7 text-[var(--theme-dark-soft)]'>{item.body}</p>
      </article>
    ))}
  </div>
);

export const StatsGrid = ({
  items,
}: {
  items: Array<{ value: string; label: string; detail?: string }>;
}) => (
  <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-4'>
    {items.map((item) => (
      <article
        key={`${item.value}-${item.label}`}
        className='rounded-[1.75rem] border border-[rgba(51,53,51,0.15)] bg-[linear-gradient(180deg,var(--theme-paper-warm)_0%,var(--theme-surface)_100%)] p-6 shadow-[0_12px_32px_rgba(32,32,32,0.06)]'
      >
        <div className='text-4xl font-black tracking-tight text-[var(--theme-dark)]'>
          {item.value}
        </div>
        <div className='mt-2 text-sm font-bold uppercase tracking-[0.18em] text-[var(--theme-dark)]'>
          {item.label}
        </div>
        {item.detail && (
          <p className='mt-3 text-base leading-7 text-[var(--theme-dark-soft)]'>
            {item.detail}
          </p>
        )}
      </article>
    ))}
  </div>
);

export const BulletColumns = ({
  items,
}: {
  items: Array<{ title: string; bullets: string[] }>;
}) => (
  <div className='grid gap-4 lg:grid-cols-2'>
    {items.map((item) => (
      <article
        key={item.title}
        className='rounded-[1.75rem] border border-[rgba(51,53,51,0.15)] bg-[linear-gradient(180deg,var(--theme-paper-warm)_0%,var(--theme-surface)_100%)] p-6 shadow-[0_12px_32px_rgba(32,32,32,0.06)]'
      >
        <h3 className='text-xl font-black text-[var(--theme-dark)]'>{item.title}</h3>
        <ul className='mt-4 space-y-3 text-base leading-7 text-[var(--theme-dark-soft)]'>
          {item.bullets.map((bullet) => (
            <li key={bullet} className='flex gap-3'>
              <span className='mt-2 h-2 w-2 rounded-full bg-[var(--theme-accent)]' />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      </article>
    ))}
  </div>
);

export const CtaPanel = ({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action: Action;
}) => (
  <section className='px-4 py-12 sm:px-6 lg:px-8'>
    <div className='mx-auto max-w-7xl rounded-[2rem] bg-[linear-gradient(135deg,var(--theme-dark)_0%,var(--theme-dark-soft)_100%)] px-6 py-10 text-white shadow-[0_30px_80px_rgba(32,32,32,0.18)] md:px-10'>
      <div className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
        <div className='max-w-3xl text-center md:text-left'>
          <h2 className='text-[1.75rem] font-black tracking-tight md:text-4xl'>
            {title}
          </h2>
          <p className='mt-4 text-lg leading-8 text-[var(--theme-light)]'>{body}</p>
        </div>
        <ActionLink {...action} />
      </div>
    </div>
  </section>
);
